/**
 * @file UploadAutomationFiles.jsx
 * @description Read-only history of what the tracker has claimed for one
 *              automation. Each row here is the reason a re-dropped file is
 *              ignored rather than ingested a second time.
 * @component UploadAutomationFiles
 * @exports UploadAutomationFiles
 */

import React, { useEffect, useState } from "react";
import {
    Box, CircularProgress, Dialog, DialogContent, DialogTitle,
    IconButton, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
    AUTOMATION_STRINGS, FILE_STATUS_LABEL,
} from "../../common/constants/uploadAutomationModule/uploadAutomationConstants.js";
import { fetchAutomationFiles } from "../../services/uploadAutomationModule/uploadAutomationServices.js";

const BLUE  = "#378ADD";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const PINK  = "#D4537E";
const MONO  = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const STATUS_COLOR = {
    completed:  GREEN,
    processing: BLUE,
    claimed:    BLUE,
    failed:     PINK,
    skipped:    AMBER,
};

const formatDateTime = (val) => {
    const d = val ? new Date(val) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${mm}/${dd} ${hh}:${mi}:${ss}`;
};

function StatusTag({ status }) {
    const color = STATUS_COLOR[status] || "text.secondary";
    return (
        <Box component="span" sx={{
            fontSize: 10, fontWeight: 500, letterSpacing: "0.04em",
            textTransform: "uppercase",
            px: "6px", py: "2px", borderRadius: "4px",
            color, border: "0.5px solid", borderColor: color,
            whiteSpace: "nowrap",
        }}>
            {FILE_STATUS_LABEL[status] || status}
        </Box>
    );
}

const UploadAutomationFiles = ({ open, config, onClose }) => {
    const [files,   setFiles]   = useState([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState("");

    useEffect(() => {
        let cancelled = false;
        if (!open || !config?.configId) return undefined;
        (async () => {
            try {
                setLoading(true); setError("");
                const list = await fetchAutomationFiles(config.configId, 50);
                if (!cancelled) setFiles(list);
            } catch (e) {
                console.error(e);
                if (!cancelled) setError(AUTOMATION_STRINGS.FILES_FAILED);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, config?.configId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}>

            <DialogTitle sx={{
                fontSize: 15, fontWeight: 500, py: 1.5,
                borderBottom: "0.5px solid", borderColor: "divider",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                <Box sx={{ minWidth: 0 }}>
                    {AUTOMATION_STRINGS.FILE_HISTORY}
                    <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 400 }}>
                        {config?.name}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5 }}>
                    {AUTOMATION_STRINGS.DEDUPE_NOTE}
                </Typography>

                {loading && (
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", py: 2 }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading…</Typography>
                    </Box>
                )}

                {!loading && error && (
                    <Typography sx={{ fontSize: 12, color: PINK }}>{error}</Typography>
                )}

                {!loading && !error && files.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: "text.secondary", py: 2, textAlign: "center" }}>
                        {AUTOMATION_STRINGS.NO_FILES}
                    </Typography>
                )}

                {!loading && files.length > 0 && (
                    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 1 }}>
                        {files.map((f, i) => (
                            <Box key={f.fileId} sx={{
                                display: "flex", alignItems: "center", gap: 1,
                                px: 1.5, py: "8px",
                                borderTop: i === 0 ? "none" : "0.5px solid",
                                borderColor: "divider",
                                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                            }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box sx={{
                                        fontSize: 12, fontFamily: MONO, color: "text.primary",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>
                                        {f.fileName}
                                    </Box>
                                    <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                                        {formatDateTime(f.claimedAt)}
                                        {f.rowCountLoaded != null && ` · ${f.rowCountLoaded.toLocaleString()} rows`}
                                        {f.attemptCount > 1 && ` · attempt ${f.attemptCount}`}
                                    </Box>
                                </Box>
                                {f.errorMessage ? (
                                    <Tooltip title={f.errorMessage} arrow>
                                        <Box><StatusTag status={f.status} /></Box>
                                    </Tooltip>
                                ) : (
                                    <StatusTag status={f.status} />
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UploadAutomationFiles;